/**
 * Concern:
 * Shared request-layer helpers for timeout handling, network error classification, and generic error formatting.
 */
export const RETRYABLE_NETWORK_ERROR_MESSAGE_PATTERNS = [
  /fetch failed/i,
  /network/i,
  /timed? out/i,
  /socket/i,
  /econnreset/i,
  /econnrefused/i,
  /etimedout/i,
  /enotfound/i,
  /eai_again/i,
  /service unavailable/i,
];

const DEFAULT_RETRYABLE_NETWORK_ERROR_NAMES = new Set([
  "AbortError",
  "RequestTimeoutError",
]);

export class RequestTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RequestTimeoutError";
  }
}

export const formatUnknownError = (error: unknown) =>
  error instanceof Error ? error.message : "Unknown error";

export const withRequestTimeout = async <T>({
  operation,
  timeoutMs,
  timeoutMessage,
}: {
  operation: () => Promise<T>;
  timeoutMs: number;
  timeoutMessage: string;
}) => {
  if (timeoutMs <= 0) {
    return await operation();
  }

  return await new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new RequestTimeoutError(timeoutMessage));
    }, timeoutMs);

    operation()
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
};

export const isRetryableNetworkRequestError = (
  error: unknown,
  options?: {
    retryableErrorNames?: ReadonlySet<string>;
    messagePatterns?: readonly RegExp[];
  },
) => {
  if (!(error instanceof Error)) {
    return false;
  }

  const retryableErrorNames =
    options?.retryableErrorNames ?? DEFAULT_RETRYABLE_NETWORK_ERROR_NAMES;
  const errorName = (error as { name?: string }).name;
  if (errorName && retryableErrorNames.has(errorName)) {
    return true;
  }

  const messagePatterns =
    options?.messagePatterns ?? RETRYABLE_NETWORK_ERROR_MESSAGE_PATTERNS;
  return messagePatterns.some((pattern) => pattern.test(error.message));
};
